"use client";

import { c as _c } from "react/compiler-runtime";
import CloseIcon from "@/assets/icons/close.svg";
import { ScrollArea } from "../scroll-area/scroll-area";
import { useDrawerResize } from "@/shared/hooks";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { Avatar, Drawer, Loader } from "@mantine/core";
import { useEffect, type ReactNode } from "react";
import classes from "./detail-drawer.module.scss";
const DEFAULT_DESKTOP_WIDTH = 421;
export type DetailDrawerProps = Readonly<{
  opened: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  avatar?: {
    src?: string | null;
    alt?: string;
    initials: string;
  };
  children: ReactNode;
  footer: ReactNode;
  isSaving?: boolean;
  /** Extra content below scroll area. */
  afterScroll?: ReactNode;
  /** Desktop drawer width in px. Defaults to 421. */
  size?: number;
  closeOnClickOutside?: boolean;
}>;
export type DetailDrawerFooterActionsProps = Readonly<{
  children: ReactNode;
}>;
export type DetailDrawerFooterButtonProps = Readonly<{
  children: ReactNode;
}>;

/**
 * Detail drawer shell shared by account and admin form sidebars.
 */
export function DetailDrawer(t0) {
  const $ = _c(64);
  const {
    opened,
    onClose,
    title,
    subtitle,
    avatar,
    children,
    footer,
    isSaving: t1,
    afterScroll,
    size: t2,
    closeOnClickOutside: t3
  } = t0;
  const isSaving = t1 === undefined ? false : t1;
  const size = t2 === undefined ? DEFAULT_DESKTOP_WIDTH : t2;
  const closeOnClickOutside = t3 === undefined ? true : t3;
  const isMobile = useIsMobile();
  const {
    handle,
    reset,
    drawerStyles
  } = useDrawerResize(onClose);
  let t4;
  let t5;
  if ($[0] !== isMobile || $[1] !== opened || $[2] !== reset) {
    t4 = () => {
      if (opened && isMobile) {
        reset();
      }
    };
    t5 = [opened, isMobile, reset];
    $[0] = isMobile;
    $[1] = opened;
    $[2] = reset;
    $[3] = t4;
    $[4] = t5;
  } else {
    t4 = $[3];
    t5 = $[4];
  }
  useEffect(t4, t5);
  const t6 = isMobile ? "bottom" : "right";
  const t7 = isMobile ? undefined : size;
  let t8;
  if ($[5] !== drawerStyles || $[6] !== isMobile || $[7] !== size) {
    t8 = isMobile ? drawerStyles : {
      content: {
        maxWidth: size,
        width: size
      }
    };
    $[5] = drawerStyles;
    $[6] = isMobile;
    $[7] = size;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const t9 = isMobile ? classes.overlayMobile : classes.overlayDesktop;
  const t10 = isMobile ? classes.sheetContentMobile : classes.sheetContentDesktop;
  let t11;
  if ($[9] !== t10) {
    t11 = cn(classes.sheetContent, t10);
    $[9] = t10;
    $[10] = t11;
  } else {
    t11 = $[10];
  }
  let t12;
  if ($[11] !== t11 || $[12] !== t9) {
    t12 = {
      overlay: t9,
      content: t11,
      body: classes.sheetBody
    };
    $[11] = t11;
    $[12] = t9;
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  const t13 = isMobile && classes.headerMobile;
  let t14;
  if ($[14] !== t13) {
    t14 = cn(classes.header, t13);
    $[14] = t13;
    $[15] = t14;
  } else {
    t14 = $[15];
  }
  let t15;
  if ($[16] !== handle || $[17] !== isMobile) {
    t15 = isMobile && <div className={classes.dragHandleWrap}>{handle}</div>;
    $[16] = handle;
    $[17] = isMobile;
    $[18] = t15;
  } else {
    t15 = $[18];
  }
  let t16;
  if ($[19] !== avatar || $[20] !== title) {
    t16 = avatar && <Avatar src={avatar.src ?? undefined} alt={avatar.alt ?? title} radius="md" classNames={{
      root: classes.avatar,
      image: classes.avatarImage,
      placeholder: classes.avatarPlaceholder
    }}>{avatar.initials}</Avatar>;
    $[19] = avatar;
    $[20] = title;
    $[21] = t16;
  } else {
    t16 = $[21];
  }
  let t17;
  if ($[22] !== title) {
    t17 = <span className={classes.title}>{title}</span>;
    $[22] = title;
    $[23] = t17;
  } else {
    t17 = $[23];
  }
  let t18;
  if ($[24] !== subtitle) {
    t18 = subtitle ? <span className={classes.subtitle}>{subtitle}</span> : null;
    $[24] = subtitle;
    $[25] = t18;
  } else {
    t18 = $[25];
  }
  let t19;
  if ($[26] !== t17 || $[27] !== t18) {
    t19 = <div className={classes.headerText}>{t17}{t18}</div>;
    $[26] = t17;
    $[27] = t18;
    $[28] = t19;
  } else {
    t19 = $[28];
  }
  let t20;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <CloseIcon width={24} height={24} />;
    $[29] = t20;
  } else {
    t20 = $[29];
  }
  let t21;
  if ($[30] !== onClose) {
    t21 = <button type="button" onClick={onClose} className={classes.closeButton} aria-label="Close">{t20}</button>;
    $[30] = onClose;
    $[31] = t21;
  } else {
    t21 = $[31];
  }
  let t22;
  if ($[32] !== t16 || $[33] !== t19 || $[34] !== t21) {
    t22 = <div className={classes.headerRow}>{t16}{t19}{t21}</div>;
    $[32] = t16;
    $[33] = t19;
    $[34] = t21;
    $[35] = t22;
  } else {
    t22 = $[35];
  }
  let t23;
  if ($[36] !== t14 || $[37] !== t15 || $[38] !== t22) {
    t23 = <div className={t14}>{t15}{t22}</div>;
    $[36] = t14;
    $[37] = t15;
    $[38] = t22;
    $[39] = t23;
  } else {
    t23 = $[39];
  }
  let t24;
  if ($[40] !== isSaving) {
    t24 = isSaving && <div className={classes.savingOverlay}><Loader size="sm" /></div>;
    $[40] = isSaving;
    $[41] = t24;
  } else {
    t24 = $[41];
  }
  let t25;
  if ($[42] !== children) {
    t25 = <ScrollArea className={classes.scrollContent}>{children}</ScrollArea>;
    $[42] = children;
    $[43] = t25;
  } else {
    t25 = $[43];
  }
  let t26;
  if ($[44] !== afterScroll || $[45] !== t24 || $[46] !== t25) {
    t26 = <div className={classes.bodyHost}>{t24}{t25}{afterScroll}</div>;
    $[44] = afterScroll;
    $[45] = t24;
    $[46] = t25;
    $[47] = t26;
  } else {
    t26 = $[47];
  }
  const t27 = isMobile && classes.footerMobile;
  let t28;
  if ($[48] !== t27) {
    t28 = cn(classes.footer, t27);
    $[48] = t27;
    $[49] = t28;
  } else {
    t28 = $[49];
  }
  let t29;
  if ($[50] !== footer || $[51] !== t28) {
    t29 = <div className={t28}>{footer}</div>;
    $[50] = footer;
    $[51] = t28;
    $[52] = t29;
  } else {
    t29 = $[52];
  }
  let t30;
  if ($[53] !== closeOnClickOutside || $[54] !== onClose || $[55] !== opened || $[56] !== t12 || $[57] !== t23 || $[58] !== t26 || $[59] !== t29 || $[60] !== t6 || $[61] !== t7 || $[62] !== t8) {
    t30 = <Drawer opened={opened} onClose={onClose} position={t6} size={t7} withCloseButton={false} padding={0} trapFocus={false} closeOnClickOutside={closeOnClickOutside} styles={t8} classNames={t12}>{t23}{t26}{t29}</Drawer>;
    $[53] = closeOnClickOutside;
    $[54] = onClose;
    $[55] = opened;
    $[56] = t12;
    $[57] = t23;
    $[58] = t26;
    $[59] = t29;
    $[60] = t6;
    $[61] = t7;
    $[62] = t8;
    $[63] = t30;
  } else {
    t30 = $[63];
  }
  return t30;
}
export function DetailDrawerFooterActions(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className={classes.footerActions}>{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
export function DetailDrawerFooterButton(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className={classes.footerActionButton}>{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}